import Bottombar from "@/components/bottombar"
import Button from "@/components/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Icon } from "@/icons/icon"
import { UseFormReturn } from "@/lib/form/form"
import { ParamSchema } from "@/lib/form/schema"
import { SingleValueParamData, WindowData } from "@/lib/form/types"
import { cn } from "@/lib/utils"
import { ReactNode, useEffect, useState } from "react"

export interface ResampleDetailsSheetProps {
  form: UseFormReturn
}

type DetailsPayload = (WindowData | SingleValueParamData) & {
  title?: string
}

export default function ResampleDetailsSheet({
  form,
}: ResampleDetailsSheetProps) {
  const [open, setOpen] = useState(false)
  const [data, setData] = useState<DetailsPayload>()
  const [schema, setSchema] = useState<ParamSchema>()

  useInit()

  function useInit() {
    useEffect(() => {
      form.addEventHandler("clickShowResampleDetails", (__event, ctx) => {
        setData(ctx.payload)
        setSchema(ctx.schema as ParamSchema)

        if (ctx.payload) {
          setOpen(true)
        } else {
          setOpen(false)
        }
      })
    }, [form])
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="bottom"
        title="Fill remarks on machine"
        className="shadow-drop-shadow rounded-t-lg"
      >
        <SheetHeader className="flex">
          <span className="size-13 flex items-center justify-center rounded-full bg-backdrop">
            <Icon name="ResampleBadge" className="size-6" />
          </span>

          <SheetTitle className="text-xl mt-5">Resample</SheetTitle>
        </SheetHeader>

        <div className="px-4 pb-8">
          <DetailSection
            title="first reading"
            field={schema?.name}
            value={data?.value}
            remark={data?.remark}
            icon={<Icon name="RedFlag" className="h-3.5 w-auto" />}
          />

          <hr className="h-0 w-full my-4 border-t border-t-box-l-border" />

          <DetailSection
            title="final reading (after resample)"
            field={schema?.name}
            value={data?.resample?.value}
            remark={data?.resample?.remark}
            icon={<Icon name="ResampleBadge" className="h-4 w-auto" />}
          />
        </div>

        <Bottombar className="flex gap-4 bg-white">
          <Button
            variant="default"
            className="flex-1"
            type="submit"
            onClick={() => setOpen(false)}
          >
            Go Back
          </Button>
        </Bottombar>
      </SheetContent>
    </Sheet>
  )
}

export interface DetailSectionProps {
  title?: string
  field?: string
  value?: string
  remark?: string
  icon?: ReactNode
  className?: string
}

export function DetailSection({
  title,
  field,
  value,
  remark,
  icon,
  className,
}: DetailSectionProps) {
  return (
    <div>
      {title && (
        <h4 className={cn("uppercase font-medium text-sm mb-3", className)}>
          {title}
        </h4>
      )}

      <div className="flex flex-col gap-1.5">
        <h5 className="text-sm text-label-text font-medium capitalize">
          {field}
        </h5>

        <div className="flex items-center gap-3">
          <p className="font-medium text-base">{value}</p>
          {icon}
        </div>
      </div>

      {remark && (
        <p className="rounded-lg bg-backdrop p-3 w-full min-h-20 font-normal mt-4 flex gap-3">
          <span>{remark}</span>
        </p>
      )}
    </div>
  )
}
